import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { EmployeeFunctionGroup } from 'src/app/_model/employee-function-group';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';

import { EmployeeFunctionGroupsService } from '../employee-function-groups.service';
import { EmployeeFunctionGroupsDaoService } from '../../dao/employee-function-groups-dao.service';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads the ims/Employee <-> ims/FunctionGroup mapping of the caller's company
 */
@Injectable()
export class EmployeeFunctionGroupsLogicService implements EmployeeFunctionGroupsService {
  public employeeFunctionGroups$ = new BehaviorSubject<EmployeeFunctionGroup[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private employeeFunctionGroupsDaoService: EmployeeFunctionGroupsDaoService,
    private authenticationService: AuthenticationService,
  ) {
    this.initialize();
  }

  // Resetable
  public async reset() {
    // Unlike clear(), reset() also wipes locally created (not-yet-synced) mappings.
    await this.employeeFunctionGroupsDaoService.reset();
    const remaining = await this.employeeFunctionGroupsDaoService.getEmployeeFunctionGroups();
    this.employeeFunctionGroups$.next(remaining);
  }

  public async clear() {
    // Only truncates the synced table (see DAO) - reload so locally created
    // mappings that survive the clear stay visible in the BehaviorSubject.
    await this.employeeFunctionGroupsDaoService.clear();
    const remaining = await this.employeeFunctionGroupsDaoService.getEmployeeFunctionGroups();
    this.employeeFunctionGroups$.next(remaining);
  }

  /**
   * Creates a new, purely local Employee<->FunctionGroup mapping (not sent to the backend).
   */
  public async createEmployeeFunctionGroup(entry: EmployeeFunctionGroup): Promise<EmployeeFunctionGroup> {
    const created = await this.employeeFunctionGroupsDaoService.createEmployeeFunctionGroup(entry);
    if (created) {
      this.employeeFunctionGroups$.next([...this.employeeFunctionGroups$.getValue(), created]);
    }
    return created;
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemoteEmployeeFunctionGroups(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalEmployeeFunctionGroups();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  async initializeCompleted(): Promise<void> {}

  // Private Functions
  private async loadLocalEmployeeFunctionGroups(): Promise<void> {
    try {
      this.logger.log(this.constructor.name, '- Load local employee function groups');
      const entries = await this.employeeFunctionGroupsDaoService.getEmployeeFunctionGroups();
      this.employeeFunctionGroups$.next(entries);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local employee function groups: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemoteEmployeeFunctionGroups(apiLevel: APILevel, syncKey: string): Promise<void> {
    if (!this.authenticationService.authenticationState.getValue()) {
      return;
    }

    // Same gate as the branch mapping: syncemployeefunctiongroups ships with the
    // same 4.32 backend as the rest of the PCD, so it needs no API level of its own.
    if (apiLevel < APILevel.PROJECT_CONTACT_DIRECTORY) {
      this.logger.log(
        this.constructor.name,
        '- Backend API is too low to sync employee function groups',
      );
      return;
    }

    this.logger.log(this.constructor.name, '- Load new employee function groups from IMS');

    try {
      const entriesData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncemployeefunctiongroups',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            synckey: syncKey,
          },
        }),
      );

      const entries: EmployeeFunctionGroup[] = entriesData.map((dto: any) =>
        EmployeeFunctionGroup.fromDto(dto),
      );

      this.logger.log(
        this.constructor.name,
        'Finished loading employee function groups (' + entries.length + ' Total)',
      );

      await this.employeeFunctionGroupsDaoService.saveEmployeeFunctionGroups(entries);
      // Reload the full merged list (synced + local) so locally created mappings stay visible.
      const merged = await this.employeeFunctionGroupsDaoService.getEmployeeFunctionGroups();
      this.employeeFunctionGroups$.next(merged);
    } catch (error) {
      // Deliberately swallowed, unlike every other syncable: this mapping has no API level of
      // its own, so a backend that doesn't know syncemployeefunctiongroups yet would otherwise
      // abort the whole sync chain here - and everything after it (the project contact directory
      // itself) would never be synced. Without the mapping the employee picker just falls back
      // to showing everyone.
      this.logger.error(
        this.constructor.name,
        'Error downloading employee function groups (continuing without them): ',
        error?.message,
      );
    }
  }
}
